import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MinimoveFaceGallery.css";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import {
  getImageBlobFromDB,
  saveImageBlobToDB,
  FACE_STORE_NAME,
} from "../../../../IndexedDBUtils";
import { showAlert } from "../../../../generalUtils";
import { useFaceGalleryEngine } from "../../shared/useFaceGalleryEngine";
import {
  MSG_LOST,
  MSG_RELEASE_UNCONFIRMED,
  messageForAcquire,
  useFaceClaim,
} from "../../shared/useFaceClaim";
import { returnHomeAfterAdminRelease } from "../../shared/adminRelease";
import HeldFaceOverlay from "../../shared/HeldFaceOverlay";
import HeldGalleryStatus, { useHeldSelectionGuard } from "../../shared/HeldGalleryStatus";
import { FaceGalleryEmpty, FaceGallerySyncChip } from "../../shared/FaceGallerySyncStatus";
import { SERVER_URL } from "../../../../config/serverUrl";
// import { useGif } from "../../../../GifContext";
// import gifFailed from "../../../../assets/gifs/result-failed.gif";

const DOUBLE_CLICK_DELAY = 250; // Delay to differentiate single and double click (ms)

const MinimoveFaceGalleryPage = () => {
  // const { showGif } = useGif();
  const serverUrl = SERVER_URL;
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  // In Minimove, we always have only 1 face
  const number = 1;

  // The held list and the selection guard need each other (claim -> engine setters, engine -> claim
  // state), so the engine reaches the claim through a ref.
  const claimRef = useRef(null);
  const {
    adminPreview,
    eventName,
    imagePaths,
    selectedImages,
    setSelectedImages,
    timeLeft,
    formatTimeLeft,
    toggleImageSelection,
    syncError,
    isEmpty,
  } = useFaceGalleryEngine({
    maxSelectable: number,
    hideLoadingOnInit: true,
    onCycle: () => claimRef.current && claimRef.current.refreshHeld(),
    isBlocked: (path) =>
      Boolean(claimRef.current && claimRef.current.isBlockedByOther(path)),
  });
  const claim = useFaceClaim({
    adminPreview,
    onLost: (reason) => {
      setSelectedImages([]);
      // Operator release: the guest starts over at Home (expiry keeps the old in-gallery notice).
      if (reason === "revoked") returnHomeAfterAdminRelease(navigate);
      else showAlert("err", MSG_LOST, 5000);
    },
    onRestore: (faces) => setSelectedImages(faces.slice(0, number)),
    onReleaseFailed: () => showAlert("err", MSG_RELEASE_UNCONFIRMED, 5000),
  });
  claimRef.current = claim;
  const heldNotice = useHeldSelectionGuard({ selectedImages, setSelectedImages, claim, adminPreview });

  const clickTimeoutRef = useRef(null);
  const selectedRef = useRef(selectedImages);
  selectedRef.current = selectedImages;
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    };
  }, []);

  // Next (button or double-tap): the hold is acquired HERE; navigation only on editing/shared.
  const attemptNext = async (selection) => {
    if (submitting) return;
    setSubmitting(true);
    const result = await claim.acquireForSelection(selection);
    if (mountedRef.current) setSubmitting(false);
    if (
      result.status === "stale" ||
      result.status === "busy" ||
      result.status === "flow_expired" ||
      !mountedRef.current
    )
      return;
    if (result.proceed) {
      goToBodyGallery(selection);
    } else if (result.status === "revoked") {
      returnHomeAfterAdminRelease(navigate);
    } else {
      showAlert("err", messageForAcquire(result), 5000);
      if (result.status === "held") {
        setSelectedImages((current) =>
          current.filter((path) => !(result.heldFaces || []).includes(path))
        );
      }
      claim.refreshHeld(); // reconcile at once with the server's view
    }
  };

  // Handle single vs double click on an image. A single click toggles
  // selection after a short delay. A second click within that delay will
  // ensure the image is selected and immediately go for the body gallery
  // (through the claim acquire).
  const handleImageClick = (imagePath, isSelectable) => {
    if (!isSelectable) return;

    // Second click within delay -> treat as double click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;

      // Compute the updated selection immediately so we can navigate
      const current = selectedRef.current;
      let newSelection = current;
      if (!current.includes(imagePath)) {
        newSelection = [...current, imagePath].slice(-number);
        setSelectedImages(newSelection);
      }
      // Acquire, then navigate using the updated selection
      attemptNext(newSelection);
    } else {
      // First click -> wait to see if a second click comes
      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        toggleImageSelection(imagePath);
      }, DOUBLE_CLICK_DELAY);
    }
  };

  // Navigate to the body gallery page (enhanced with fallback recovery)
  const goToBodyGallery = async (overrideSelection) => {
    showLoading();
    try {
      const imagesToUse = overrideSelection || selectedImages;

      if (imagesToUse.length === 0) {
        throw new Error("No faces selected");
      }

      const path = imagesToUse[0]; // In miniMove, we always have only 1 face

      // Enhanced validation: Check if the image exists in IndexedDB
      let existingImage = await getImageBlobFromDB(
        eventName,
        FACE_STORE_NAME,
        path
      );

      // Fallback recovery: If image not found in IndexedDB, try to reload from server
      if (!existingImage) {
        console.warn(
          `Image ${path} not found in IndexedDB, attempting server fallback...`
        );

        try {
          const response = await fetch(
            `${serverUrl}/image${path}?size=thumbnail`
          );
          if (response.ok) {
            const blob = await response.blob();

            // Validate the blob before saving
            if (blob && blob.size > 0) {
              await saveImageBlobToDB(eventName, FACE_STORE_NAME, path, blob);
              existingImage = blob;
            }
          }
        } catch (serverError) {
          console.error(`Server fallback failed for ${path}:`, serverError);
        }
      }

      // Final validation
      if (
        !existingImage ||
        (existingImage instanceof Blob && existingImage.size === 0)
      ) {
        throw new Error("Selected face not found and could not be recovered");
      }

      // Save the path and navigate
      sessionStorage.setItem("selectedFaces", JSON.stringify(imagesToUse));
      navigate(`/${eventName}/body-gallery`);
    } catch (error) {
      console.error("Error in goToBodyGallery:", error);

      // Enhanced user feedback with constructive guidance
      let errorMessage;
      let shouldClearSelection = true;

      if (error.message.includes("could not be recovered")) {
        errorMessage =
          "התמונה אינה זמינה כרגע. אנא בחרו תמונה אחרת או נסו לרענן את הדף.";
      } else if (error.message.includes("No faces selected")) {
        errorMessage = "אנא בחרו תמונה לפני המשך.";
        shouldClearSelection = false; // Don't clear if nothing was selected
      } else {
        // General fallback - try recovery first
        errorMessage =
          "בחירת תמונה נכשלה. נסו לבחור תמונה אחרת או לרענן את הדף.";
      }

      showAlert("err", errorMessage, 4000);

      // Only clear selection if it makes sense to do so
      if (shouldClearSelection) {
        sessionStorage.removeItem("selectedFaces");
        setSelectedImages([]);
      }

      // Hide loading spinner
      hideLoading();
    }
  };

  // Navigate back to the home screen (explicit leave: the editing hold is released, best effort)
  const goBack = async () => {
    showLoading();
    claim.releaseNow();
    navigate(`/${eventName}`);
  };

  return (
    <div
      className={`minimove-facegallery-container${
        adminPreview ? " minimove-facegallery--preview" : ""
      }`}
    >
      {!adminPreview && (
      <div className="minimove-facegallery-header">
        <div className="minimove-facegallery-timer">
          <span className="minimove-facegallery-timer-text">
            הזמן שנותר לבחירה:
          </span>
          <span
            className={`minimove-facegallery-timer-clock ${
              timeLeft < 30 ? "minimove-facegallery-timer-clock-low" : ""
            }`}
          >
            {formatTimeLeft()}
          </span>
        </div>
        <h1 className="minimove-facegallery-title rtl">בחרו את הראש שלכם</h1>
      </div>
      )}
      <HeldGalleryStatus claim={claim} notice={heldNotice} adminPreview={adminPreview} />
      <FaceGallerySyncChip show={syncError} />
      {isEmpty ? (
        <FaceGalleryEmpty />
      ) : (
      <div className="minimove-facegallery-grid">
        {imagePaths.map(({ path, image }, index) => {
          const isSelected = selectedImages.includes(path);
          // A head another guest holds ("תפוס") can never be selected (a selected one is dropped by the guard).
          const isHeld = claim.isBlockedByOther(path);
          const isSelectable =
            !isHeld && (selectedImages.length < number || isSelected);
          return (
            <div
              key={index}
              className={`minimove-facegallery-image-container ${
                isSelected ? "minimove-facegallery-image-selected" : ""
              } ${
                !isSelectable && !isHeld ? "minimove-facegallery-image-unselectable" : ""
              }`}
              aria-disabled={isHeld ? "true" : undefined}
              data-held={isHeld ? "true" : undefined}
              onClick={() => handleImageClick(path, isSelectable)}
            >
              <LazyLoadImage
                src={image}
                alt={`Face ${index}`}
                className="minimove-facegallery-image"
                effect="blur"
              />
              {isHeld && <HeldFaceOverlay state={claim.heldStateOf(path)} />}
              {isSelected && (
                <div className="minimove-facegallery-image-overlay">
                  <span className="text-white text-2xl">✔</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
      {!adminPreview && (
      <div className="minimove-facegallery-button-container">
        <button className="minimove-facegallery-button rtl" onClick={goBack}>
          חזרה
        </button>
        <button
          className={`minimove-facegallery-button rtl ${
            selectedImages.length === number
              ? ""
              : "minimove-facegallery-button-disabled"
          }`}
          onClick={() => attemptNext(selectedImages)}
          disabled={selectedImages.length !== number || submitting}
        >
          הבא
        </button>
      </div>
      )}
    </div>
  );
};

export default MinimoveFaceGalleryPage;
